import React, {FC, useCallback, useMemo, useState} from 'react';
import {FormikHelpers} from 'formik/dist/types';
import {Form} from 'forms';
import {IWithChildren} from 'models/common';
import {useFileEdit, useFileIdParam, useFileType} from '../../hooks';
import {IEditFileMetadataFormBaseValues, buildEditFileMetadataValidationSchema} from './config';
import {mapEditFileInitialValues} from '../../utils';
import {runResourceFormSubmit, snapshotResourceFormValues} from 'admin/resources/utils';
import {usePermissions} from 'permissions';
import {SuperAndNationalAdminroles} from 'enums/admin-roles';

type IFilesEditResourceFormProps = IWithChildren;

const emptyFileTypeRecognition = {
  isResource: false,
  isScormPackage: false,
  isWebLink: false,
  isArchive: false,
  isVideo: false,
  isAudio: false,
  isImage: false,
  isPDF: false,
  isFillablePdf: false,
  isDocument: false,
  isExcel: false,
};

export const FilesEditResourceForm: FC<IFilesEditResourceFormProps> = ({children}) => {
  const {updateResource, fileDetails} = useFileEdit();
  const {fileId} = useFileIdParam();
  const fileType = useFileType();
  const {hasPermission} = usePermissions();

  const hasAdminPermission = hasPermission(SuperAndNationalAdminroles);

  // Only bind the current route's record so a prior resource cannot seed the form.
  const resolvedFileDetails = fileDetails && String(fileDetails.id) === String(fileId) ? fileDetails : null;
  const resolvedFileMetadata = resolvedFileDetails?.metadata ?? null;

  const initialValues = useMemo(
    () =>
      mapEditFileInitialValues(
        resolvedFileDetails,
        resolvedFileMetadata,
        resolvedFileDetails ? fileType : emptyFileTypeRecognition,
        hasAdminPermission
      ),
    // Parent remounts via key when the record loads; do not recreate initialValues on
    // unrelated store updates (lookup fetches) or Formik would wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedFileDetails?.id, hasAdminPermission]
  );

  const [restoreKey, setRestoreKey] = useState(0);
  const [restoreValues, setRestoreValues] = useState<IEditFileMetadataFormBaseValues | null>(null);

  const submitHandler = useCallback(
    async (formValues: IEditFileMetadataFormBaseValues, helpers: FormikHelpers<IEditFileMetadataFormBaseValues>) => {
      const snapshot = snapshotResourceFormValues(formValues);
      try {
        await runResourceFormSubmit(formValues, helpers, () => updateResource(formValues, fileType));
      } catch {
        setRestoreValues(snapshot);
        setRestoreKey((key) => key + 1);
      }
    },
    [updateResource, fileType]
  );

  const validationSchema = useMemo(
    () =>
      buildEditFileMetadataValidationSchema(
        resolvedFileDetails?.details?.createdUtc ?? resolvedFileDetails?.createdUtc
      ),
    [resolvedFileDetails?.details?.createdUtc, resolvedFileDetails?.createdUtc]
  );

  return (
    <Form.Form<IEditFileMetadataFormBaseValues>
      key={restoreKey}
      initialValues={restoreValues ?? initialValues}
      validationSchema={validationSchema}
      submitHandler={submitHandler}
      enableReinitialize={false}
      disableNativeSubmit
    >
      {children}
    </Form.Form>
  );
};
